'use client'

import { useEffect, useState } from 'react'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowUpDownIcon, PlusIcon, RefreshCw, Search } from 'lucide-react'

import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
  toast,
} from '@ui/web/components'
import { createSwapy, typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { BookGalleryCard } from '../../../components'
import { useSharedTRPC } from '../../../context'
import { MyBookCollectionForm } from '../../../forms'
import { Empty } from './empty'
import { NoResultFound } from './no-result-found'

export interface AllMyCollectionsProps {
  type: 'web' | 'school'
  className?: string
  renderLink: RenderLink
}

export function AllMyCollections({ type, className, renderLink }: AllMyCollectionsProps) {
  const trpc = useSharedTRPC()

  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, { wait: 800 })

  const [openMakeNewBookCollectionDialog, setOpenMakeNewBookCollectionDialog] = useState(false)
  const [isSwapping, setIsSwapping] = useState(false)
  const [isReordering, setIsReordering] = useState(false)
  const [newOrderData, setNewOrderData] = useState<string | null>(null)

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    trpc.myBookCollection.getAllBookCollections.infiniteQueryOptions(
      { search: debouncedSearch, pageSize: 10 },
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
  )

  const { mutate: setOrder } = useMutation(trpc.myBookCollection.setOrder.mutationOptions())

  useEffect(() => {
    if (!isReordering || !data) return
    const container = document.querySelector('.collections-grid')
    if (!container) return

    const swapy = createSwapy(container, {
      swapMode: 'hover',
    })

    swapy.onSwapStart(() => setIsSwapping(true))

    swapy.onSwap(() => setIsSwapping(true))

    swapy.onSwapEnd(({ data: swapyData, hasChanged }) => {
      setIsSwapping(false)

      if (hasChanged) {
        setNewOrderData(JSON.stringify(swapyData.object))
      }
    })

    return () => swapy.destroy()
  }, [isReordering, data])

  return (
    <>
      <main className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-8', className)}>
        <header className='srd:flex srd:flex-col srd:gap-y-4'>
          <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-4'>
            <h2 className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-white')}>
              Kompilasiku
            </h2>

            <InputGroup className='srd:w-full srd:sm:max-w-[300px] srd:rounded-full'>
              <InputGroupAddon align='inline-start'>
                <Search />
              </InputGroupAddon>
              <InputGroupInput
                placeholder='Cari koleksi...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={isReordering}
              />
            </InputGroup>
          </div>

          <section className='srd:w-fit srd:h-fit srd:flex srd:items-center srd:gap-x-4'>
            <Button variant='secondary' onClick={() => setOpenMakeNewBookCollectionDialog(true)}>
              <PlusIcon className='srd:w-4 srd:h-4 srd:mr-2' />
              Susun koleksi baru
            </Button>

            <Button
              variant='outline'
              className='srd:text-neutral-white'
              disabled={search.length > 0 || (data?.pages.flatMap((page) => page.data).length ?? 0) <= 1}
              onClick={() => {
                if (!isReordering) {
                  setIsReordering(true)
                  return
                }

                if (newOrderData) {
                  const toastId = toast.loading('Menyimpan susunan koleksi...')
                  setOrder(
                    { order: JSON.parse(newOrderData) as Record<string, string> },
                    {
                      onSuccess: () => {
                        toast.dismiss(toastId)
                        toast.success('Susunan koleksi berhasil disimpan')
                        setNewOrderData(null)
                      },
                      onError: () => {
                        toast.dismiss(toastId)
                        toast.error('Gagal menyimpan susunan koleksi')
                      },
                    },
                  )
                }
                setIsReordering(false)
              }}
            >
              <ArrowUpDownIcon className='srd:w-4 srd:h-4 srd:mr-2' />
              {isReordering ? 'Selesai' : 'Susun'}
            </Button>
          </section>
        </header>

        {(() => {
          if (isError) {
            return (
              <div className='srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:min-h-[40dvh] srd:text-center srd:py-10 srd:text-neutral-white'>
                <p className={cn(typographyVariants({ variant: 'title-md' }))}>Oops, Terjadi Kesalahan</p>
                <p className={cn(typographyVariants({ variant: 'body-lg' }))}>
                  Gagal memuat koleksi. Silakan coba lagi.
                </p>
                <Button onClick={() => void refetch()} variant='outline' className='srd:text-neutral-white'>
                  <RefreshCw className='srd:size-4 srd:mr-2' />
                  Coba Lagi
                </Button>
              </div>
            )
          }

          if (isPending) {
            return (
              <section className='srd:w-full srd:mt-10 srd:pb-20 srd:grid srd:2xl:grid-cols-6 srd:xl:grid-cols-5 srd:lg:grid-cols-4 srd:md:grid-cols-3 srd:sm:grid-cols-2 srd:grid-cols-1 srd:gap-12'>
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton
                    key={`skeleton-my-collection-${index}`}
                    className='srd:w-full srd:h-[200px] srd:rounded-lg srd:aspect-square'
                  />
                ))}
              </section>
            )
          }

          const allRows = data.pages.flatMap((page) => page.data)

          if (!debouncedSearch && allRows.length === 0) {
            return <Empty type={type} />
          }

          if (allRows.length === 0) {
            return <NoResultFound type={type} keyword={debouncedSearch} />
          }

          return (
            <>
              <section className='collections-grid srd:w-full srd:grid srd:2xl:grid-cols-6 srd:xl:grid-cols-5 srd:lg:grid-cols-4 srd:md:grid-cols-3 srd:sm:grid-cols-2 srd:grid-cols-2 srd:gap-12'>
                {allRows.map((item, index) => {
                  function CardItem() {
                    return (
                      <BookGalleryCard
                        collection={item.myBookCollectionToBooks}
                        animate={
                          isReordering
                            ? {
                                rotate: [-1, 1, -1],
                                transition: {
                                  repeat: Infinity,
                                  duration: 0.3,
                                },
                              }
                            : {
                                rotate: 0,
                                transition: {
                                  duration: 0.2,
                                },
                              }
                        }
                        onClick={(e) => {
                          if (isReordering) {
                            e.preventDefault()
                            e.stopPropagation()
                            return
                          }
                          if (isSwapping) {
                            e.preventDefault()
                            e.stopPropagation()
                          }
                        }}
                      >
                        <p
                          className={cn(
                            typographyVariants({ variant: 'label-lg' }),
                            'srd:text-neutral-white srd:line-clamp-2 srd:text-left',
                          )}
                        >
                          {item.name}
                        </p>
                        <p
                          className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-left srd:text-navy-40')}
                        >
                          {item.myBookCollectionToBooks.length} buku
                        </p>
                      </BookGalleryCard>
                    )
                  }

                  return (
                    <div className='srd:slot' data-swapy-slot={index} key={`slot-${index}`}>
                      <div className='srd:item' data-swapy-item={item.id}>
                        {isReordering || isSwapping ? (
                          <CardItem />
                        ) : (
                          renderLink({
                            meta: {
                              collectionId: item.id,
                            },
                            key: `collection-card-${item.id}`,
                            children: <CardItem />,
                          })
                        )}
                      </div>
                    </div>
                  )
                })}
              </section>

              {hasNextPage && (
                <div className='srd:w-full srd:flex srd:justify-center srd:pt-4'>
                  <Button
                    variant='outline'
                    onClick={() => void fetchNextPage()}
                    disabled={isFetchingNextPage}
                    loading={isFetchingNextPage}
                    className={cn(
                      'srd:rounded-full',
                      type === 'web' && 'srd:border-neutrals-white srd:text-neutrals-white',
                      type === 'school' && 'srd:border-main-blue-100 srd:text-main-blue-100',
                    )}
                  >
                    Tampilkan lebih banyak
                  </Button>
                </div>
              )}
            </>
          )
        })()}
      </main>

      <Dialog open={openMakeNewBookCollectionDialog} onOpenChange={setOpenMakeNewBookCollectionDialog} modal>
        <DialogContent className='srd:w-full srd:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle>Buat Koleksi</DialogTitle>

            <DialogDescription className='srd:sr-only'>
              Buat koleksi baru untuk memudahkan pencarian buku.
            </DialogDescription>
          </DialogHeader>

          <MyBookCollectionForm
            onFinishSubmitting={async (data) => {
              setOpenMakeNewBookCollectionDialog(false)

              if (data.type === 'create') {
                toast.success('Koleksi berhasil dibuat!')
              } else {
                toast.success('Koleksi berhasil diperbarui!')
              }

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getAllBookCollections.infiniteQueryKey(),
              })

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getAllBookCollections.queryKey(),
              })

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getOneBookCollection.queryKey({ id: data.id }),
              })
            }}
            onCancel={() => setOpenMakeNewBookCollectionDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
